import React, { useEffect, useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { getGemeenschappenForUser } from '../services/gemeenschappen';
import './Navigation.css';

/**
 * Navigation Component
 * Tabbed navigation for the main app sections
 */
const Navigation = () => {
  const { user, isAdmin } = useAuth();
  const location = useLocation();
  const [isVerantwoordelijke, setIsVerantwoordelijke] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const userEmail = user?.email || '';

  const canSeeGebruikers = isAdmin;
  const canSeeLeden = isAdmin || isVerantwoordelijke;
  const canSeeConviventies = isAdmin || isVerantwoordelijke;

  useEffect(() => {
    let isCancelled = false;

    const loadPermissions = async () => {
      if (isAdmin) {
        if (!isCancelled) setIsVerantwoordelijke(true);
        return;
      }

      if (!userEmail) {
        if (!isCancelled) setIsVerantwoordelijke(false);
        return;
      }

      // Reset while loading non-admin permissions for the current user.
      if (!isCancelled) setIsVerantwoordelijke(false);

      try {
        const gemeenschappen = await getGemeenschappenForUser(userEmail);
        if (!isCancelled) {
          setIsVerantwoordelijke(gemeenschappen.length > 0);
        }
      } catch (error) {
        console.error('Error loading verantwoordelijke navigation permissions:', error);
        if (!isCancelled) {
          setIsVerantwoordelijke(false);
        }
      }
    };

    loadPermissions();

    return () => {
      isCancelled = true;
    };
  }, [isAdmin, userEmail]);

  // Close mobile menu whenever route changes.
  useEffect(() => {
    setIsMenuOpen(false);
  }, [location.pathname]);

  const handleNavItemClick = () => {
    setIsMenuOpen(false);
  };

  return (
    <div className="main-navigation-wrapper">
      <button
        type="button"
        className="nav-toggle"
        aria-expanded={isMenuOpen}
        aria-controls="main-navigation-menu"
        onClick={() => setIsMenuOpen((open) => !open)}
      >
        <span className="nav-toggle-icon" aria-hidden="true">☰</span>
        <span className="nav-toggle-label">Menu</span>
      </button>

      <nav
        id="main-navigation-menu"
        className={`main-navigation ${isMenuOpen ? 'is-open' : ''}`}
      >
        <NavLink 
          to="/catecheses" 
          className={({ isActive }) => `nav-tab ${isActive ? 'active' : ''}`}
          onClick={handleNavItemClick}
        >
          Catecheses
        </NavLink>
        <NavLink 
          to="/gemeenschappen" 
          className={({ isActive }) => `nav-tab ${isActive ? 'active' : ''}`}
          onClick={handleNavItemClick}
        >
          Gemeenschappen
        </NavLink>
        {canSeeConviventies && (
          <NavLink
            to="/conviventies"
            className={({ isActive }) => `nav-tab ${isActive ? 'active' : ''}`}
            onClick={handleNavItemClick}
          >
            Conviventies
          </NavLink>
        )}
        {canSeeLeden && (
          <NavLink
            to="/leden"
            className={({ isActive }) => `nav-tab ${isActive ? 'active' : ''}`}
            onClick={handleNavItemClick}
          >
            Leden
          </NavLink>
        )}
        {canSeeGebruikers && (
          <NavLink 
            to="/gebruikers" 
            className={({ isActive }) => `nav-tab ${isActive ? 'active' : ''}`}
            onClick={handleNavItemClick}
          >
            Gebruikers
          </NavLink>
        )}
        <NavLink
          to="/dashboard"
          className={({ isActive }) => `nav-tab nav-tab-right ${isActive ? 'active' : ''}`}
          onClick={handleNavItemClick}
        >
          Mijn overzicht
        </NavLink>
      </nav>
    </div>
  );
};

export default Navigation;
